import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { UserPlus, AlertCircle, Eye, EyeOff, Mail, CheckCircle, RefreshCw } from 'lucide-react';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const { register } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsNoMatch'));
      return;
    }

    setLoading(true);
    try {
      const result = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        company_name: formData.company_name,
        phone: formData.phone
      });
      setRegisteredEmail(result.email || formData.email);
      setRegistered(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      const res = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registeredEmail })
      });
      if (res.ok) setResendSuccess(true);
    } catch { /* resend is best-effort */ }
    setResendLoading(false);
  };

  // Show verification notice after successful registration
  if (registered) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 pt-20">
          <div className="w-full max-w-md">
            <div data-testid="verify-email-notice" className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1a237e] rounded-full mb-4">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-[#0d1442] mb-2">{t('auth.verifyEmailTitle')}</h1>
              <p className="text-slate-600 mb-1">{t('auth.verifyEmailMessage')}</p>
              <p className="font-semibold text-[#1a237e] mb-4">{registeredEmail}</p>
              <p className="text-sm text-slate-500 mb-6">{t('auth.verifyEmailNote')}</p>

              {resendSuccess && (
                <div className="flex items-center justify-center gap-2 p-3 mb-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  {t('auth.resendSuccess')}
                </div>
              )}

              <p className="text-sm text-slate-500 mb-3">{t('auth.checkSpam')}</p>
              <button
                data-testid="resend-verification-btn"
                onClick={handleResend}
                disabled={resendLoading}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm text-[#1a237e] border border-[#1a237e] rounded-lg hover:bg-[#1a237e]/5 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${resendLoading ? 'animate-spin' : ''}`} />
                {t('auth.resendVerification')}
              </button>

              <div className="mt-6">
                <Link to="/login" className="text-sm text-slate-500 hover:text-[#1a237e] transition-colors">
                  {t('auth.backToLogin')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 pt-20 pb-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1a237e] rounded-full mb-4">
                <UserPlus className="w-8 h-8 text-white" />
              </div>
              <h1 data-testid="register-title" className="text-2xl font-bold text-[#0d1442]">{t('auth.register')}</h1>
              <p className="text-slate-500 mt-2">BIZZ Europe</p>
            </div>

            <form onSubmit={handleSubmit} data-testid="register-form">
              {error && (
                <div data-testid="register-error" className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">{t('auth.name')} *</label>
                <input
                  data-testid="register-name-input"
                  type="text" id="name" name="name" value={formData.name} onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a237e] focus:border-transparent"
                  required autoFocus
                />
              </div>

              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">{t('auth.email')} *</label>
                <input
                  data-testid="register-email-input"
                  type="email" id="email" name="email" value={formData.email} onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a237e] focus:border-transparent"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label htmlFor="company_name" className="block text-sm font-medium text-slate-700 mb-1">{t('auth.companyName')}</label>
                  <input
                    data-testid="register-company-input"
                    type="text" id="company_name" name="company_name" value={formData.company_name} onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a237e] focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">{t('auth.phone')}</label>
                  <input
                    data-testid="register-phone-input"
                    type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a237e] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">{t('auth.password')} *</label>
                <div className="relative">
                  <input
                    data-testid="register-password-input"
                    type={showPassword ? 'text' : 'password'} id="password" name="password" value={formData.password} onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a237e] focus:border-transparent pr-12"
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-1">{t('auth.confirmPassword')} *</label>
                <input
                  data-testid="register-confirm-password-input"
                  type="password" id="confirmPassword" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a237e] focus:border-transparent"
                  required
                />
              </div>

              <button
                data-testid="register-submit-button"
                type="submit" disabled={loading}
                className="w-full py-3 bg-[#1a237e] text-white font-medium rounded-lg hover:bg-[#0d1442] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '...' : t('auth.registerButton')}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-500">
              {t('auth.hasAccount')}{' '}
              <Link data-testid="login-link" to="/login" className="text-[#1a237e] font-medium hover:underline">
                {t('auth.signIn')}
              </Link>
            </div>

            <div className="mt-4 text-center">
              <Link to="/" className="text-sm text-slate-500 hover:text-[#1a237e] transition-colors">
                {t('auth.backToWebsite')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegisterPage;
